import { useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";

/** Shared streaming chat UI for the admin-only Contract Generator and Invoice Assist tools.
 * Never reachable without a valid admin JWT — entirely separate from the public ChatWidget. */
export const AdminAssistantChat = ({ mode, greeting, placeholder, onAction }) => {
  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const listRef = useRef(null);

  const sendText = async (text) => {
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("ferguson_admin_token");
      const res = await fetch(`${API}/admin/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId.current, message: text, mode }),
      });
      if (!res.ok || !res.body) throw new Error(`Assistant request failed with status ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.type === "token") {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + evt.content };
              return next;
            });
          } else if (evt.type === "action") {
            onAction?.(evt.action_type, evt.payload, evt.contract_id);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "I ran into an issue. Please try again." };
        return next;
      });
    } finally {
      setStreaming(false);
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendText(input.trim());
  };

  return (
    <div className="rounded-2xl border border-gold/15 bg-deep-card flex flex-col h-[520px]" data-testid={`admin-assistant-${mode}`}>
      <div ref={listRef} data-testid={`admin-assistant-messages-${mode}`} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[88%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-gold text-[#0A140F] font-medium" : "bg-deep border border-gold/10 text-[#D7E0DA]"
              }`}
            >
              {m.role === "assistant" ? (
                m.content ? (
                  <div className="prose-chat prose-invert">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  <Loader2 size={14} className="animate-spin text-gold" />
                )
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit} data-testid={`admin-assistant-form-${mode}`} className="shrink-0 border-t border-gold/10 p-3 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={streaming}
          data-testid={`admin-assistant-input-${mode}`}
          className="flex-1 bg-deep border border-gold/15 rounded-full px-4 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          data-testid={`admin-assistant-send-${mode}`}
          className="h-9 w-9 shrink-0 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] flex items-center justify-center transition-colors"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
};
